import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ToggleSwitch from '../../../../shared/components/ToggleSwitch';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import { IRegisterPreference } from '../dto/IRegister';
import { StepperButtons } from './StepperButtons';
import { ConfirmationModal } from '../../../../shared/components/modal/ConfirmationModal';
import { AuthService } from '../../../service';
import { ErrorToast, SuccessToast } from '../../../../utils/toasters';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { gtmService } from '../../../../utils/gtm.service';
import { FiInfo } from 'react-icons/fi';
import { CustomAutocomplete } from '../../../../shared/components/Autocomplete';
import TooltipOverlay from '../../../../shared/components/tooltip/Tooltip';
import { IRegisterStepperProps } from '../../../../model/shared/IRegistration';

export const Step7 = ({ stepIdNumber, handleBackStep }: IRegisterStepperProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const savedUser = useSelector((state: IAllStates) => state.registration);
    const [notifications, setNotifications] = useState<IRegisterPreference>({
        userId: 0,
        isNotificationByEmailRequested: true,
        isNotificationInBrowserRequested: false,
        isNotificationByMentionRequested: true,
        locale: 'en',
        isUnsubscribed: false,
    });

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>, name: string) => {
        setNotifications(prev => ({ ...prev, [name]: name === 'isUnsubscribed' ? !event : event }));
    };

    const handleNextStep = () => {
        AuthService.updateGlobalPreference({
            ...notifications,
            userId: savedUser?.id,
        })
            .then(() => {
                gtmService('brand_registration');
                navigate('/');
                SuccessToast(t('auth.register.brand.step7.success'));
            })
            .catch(error => ErrorToast(error));
    };

    useEffect(() => {
        if (savedUser?.id) {
            setNotifications(prevState => ({
                ...prevState,
                locale: savedUser.locale,
                userId: savedUser.id,
            }));
        }
    }, [savedUser?.id]);

    const changeLocaleHandler = (paramKey: string, paramValue: string | string[] | number) => {
        setNotifications(prev => ({ ...prev, [paramKey]: paramValue }));
    };

    return (
        <Form
            onSubmit={e => {
                e.preventDefault();
                handleNextStep();
            }}
        >
            <ConfirmationModal
                iconUrl="/icons/faq.png"
                title={'Are you sure you want to proceed?'}
                description={
                    "If you don't enable notifications you could miss out some important updates on your upcoming and already running campaigns."
                }
                action={handleNextStep}
                show={showConfirmModal}
                closeModal={() => setShowConfirmModal(false)}
            />
            <div>
                <h5>{t('auth.register.brand.step7.globalPreferences')}</h5>
                <p className="text-muted">{t('auth.register.brand.step7.globalPreferencesDescription')}</p>
                <div className="my-2">
                    <CustomAutocomplete
                        dataArray={['en', 'de']}
                        isWithImage={false}
                        inputLabel="locale"
                        defaultArray={notifications?.locale as string}
                        key={savedUser?.id}
                        isMultiple={false}
                        photoLabel=""
                        queryFilterKey="locale"
                        inputLabelTranslation={t('auth.register.brand.step7.language')}
                        handleChangeParams={changeLocaleHandler}
                    />
                </div>
                <h6 className="text-start mt-3 mb-0">
                    {t('brand.account.global_preferences_modal.email_notifications')}
                </h6>
                <div className="d-flex flex-column w-100">
                    <div className="d-flex justify-content-between align-items-center my-2">
                        <label
                            htmlFor="isNotificationByEmailRequested"
                            className="cursor-pointer d-flex justify-content-between align-items-center"
                        >
                            {t('brand.account.global_preferences_modal.active_campaign_notifications')}
                            <TooltipOverlay
                                className="ms-1"
                                content={t(
                                    'brand.account.global_preferences_modal.active_campaign_notifications_tooltip'
                                )}
                            >
                                <FiInfo size={14} />
                            </TooltipOverlay>
                        </label>
                        <ToggleSwitch
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                changeHandler(e, 'isNotificationByEmailRequested')
                            }
                            small
                            id="isNotificationByEmailRequested"
                            name="isNotificationByEmailRequested"
                            checked={notifications?.isNotificationByEmailRequested}
                        />
                    </div>
                    {!notifications?.isNotificationByEmailRequested && (
                        <div className="d-flex justify-content-between align-items-center my-2">
                            <label
                                htmlFor="isNotificationByMentionRequested"
                                className="cursor-pointer d-flex justify-content-between align-items-center"
                            >
                                {t('brand.account.global_preferences_modal.mentions_notifications')}
                                <TooltipOverlay
                                    className="ms-1"
                                    content={t('brand.account.global_preferences_modal.mentions_notifications_tooltip')}
                                >
                                    <FiInfo size={14} />
                                </TooltipOverlay>
                            </label>

                            <ToggleSwitch
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    changeHandler(e, 'isNotificationByMentionRequested')
                                }
                                small
                                name="isNotificationByMentionRequested"
                                id="isNotificationByMentionRequested"
                                checked={notifications?.isNotificationByMentionRequested}
                            />
                        </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center my-2">
                        <label
                            htmlFor="isUnsubscribed"
                            className="cursor-pointer d-flex justify-content-between align-items-center"
                        >
                            {t('auth.register.brand.step7.marketing_notifications')}
                        </label>
                        <ToggleSwitch
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => changeHandler(e, 'isUnsubscribed')}
                            small
                            name="isUnsubscribed"
                            id="isUnsubscribed"
                            checked={!notifications?.isUnsubscribed}
                        />
                    </div>
                </div>
            </div>
            <div className="mt-4">
                <StepperButtons
                    currentStep={stepIdNumber - 1}
                    handleNextStep={() => {
                        if (!notifications.isNotificationByEmailRequested && notifications?.isUnsubscribed) {
                            setShowConfirmModal(true);
                        } else {
                            handleNextStep();
                        }
                    }}
                    handleBackStep={handleBackStep}
                />
            </div>
        </Form>
    );
};
